import { ExternalLinkIcon, ChartBarIcon } from "@heroicons/react/outline";
import { useEffect, useMemo } from "react";
import { Link, useLocation, useSearchParams } from "react-router-dom";

import { HistoryPageNavbarLoadingShimmer } from "./HistoryPageNavbarLoadingShimmer";
import { HistoryPageNavbarMenu } from "./HistoryPageNavbarMenu";
import { HistoryPageNavbarResourceLinks } from "./HistoryPageNavbarResourceLinks";
import { HistoryPageNavbarShareDialog } from "./HistoryPageNavbarShareDialog";
import { RetentionPolicyBadge } from "./RetentionPolicyBadge";
import { useHistoryPageNavbarState } from "./hooks/useHistoryPageNavbarState";
import {
  getHistoryPageNavbarActionsClassName,
  getHistoryPageNavbarGridClassName,
  getHistoryPageNavbarMetadataClassName,
  getHistoryPageNavbarResourceLinksClassName,
  getHistoryPageNavbarTitleClassName,
} from "./historyPageNavbarLayout";
import { HistoryPageNavbarProps } from "./types";

import { BasicTooltip } from "@/components/ui/Tooltip";
import { OverflowDisplayTooltip } from "@/components/ui/Tooltip/OverflowDisplayTooltip";
import { LinkWithSearch } from "@/components/LinkWithSearch";
import { ScoreDropdown } from "@/components/ScoreDropdown";
import {
  buildFiltersFromRequestContext,
  buildWorkspaceAnalyticsPath,
} from "@/routes/request-logs/search/buildReviewAnalyticsPath";
import { createStructuredSearchDefaultDateRange } from "@/components/StructuredSearch/stores/structured-search/structured-search-store";
import { SESSION_STORAGE } from "@/constants";
import { preloadAnalyticsPage } from "@/routes/analytics/loadAnalyticsPage";
import { keepRelevantSearchParams } from "@/utils/utils";

const startOfLocalDay = (d: Date) => {
  const next = new Date(d);
  next.setHours(0, 0, 0, 0);
  return next;
};

const endOfLocalDay = (d: Date) => {
  const next = new Date(d);
  next.setHours(23, 59, 59, 999);
  return next;
};

const addCalendarDays = (start: Date, days: number) => {
  const next = new Date(start);
  next.setDate(next.getDate() + days);
  return next;
};

/** 14 inclusive calendar days: end = min(logged+7d, today) EOD, start = 12 days before end at midnight. */
const buildSimilarLogsDateRange = (requestStartTime: string | undefined) => {
  const loggedAt = requestStartTime ? new Date(requestStartTime) : null;
  if (!loggedAt || !Number.isFinite(loggedAt.getTime())) {
    return createStructuredSearchDefaultDateRange();
  }

  const loggedStartOfDay = startOfLocalDay(loggedAt);
  const loggedPlusSevenStart = startOfLocalDay(addCalendarDays(loggedStartOfDay, 7));
  const todayStart = startOfLocalDay(new Date());

  const cappedEndDayStart =
    loggedPlusSevenStart.getTime() <= todayStart.getTime() ? loggedPlusSevenStart : todayStart;

  return {
    from: startOfLocalDay(addCalendarDays(cappedEndDayStart, -12)),
    to: endOfLocalDay(cappedEndDayStart),
  };
};

const HistoryPageNavbar = ({
  requestId,
  requestInfo,
  compactTitle = false,
}: HistoryPageNavbarProps) => {
  const navbarState = useHistoryPageNavbarState({ requestId, requestInfo });
  const location = useLocation();
  const [searchParams] = useSearchParams();

  const preservedQuerySuffix = useMemo(() => {
    const qs = keepRelevantSearchParams(searchParams);
    return qs ? `?${qs}` : "";
  }, [searchParams]);

  const workspaceId = navbarState.activeWorkspaceId ?? requestInfo?.workspace_id ?? null;
  const isInTrace = location.pathname.includes("/traces/");
  const promptName = navbarState.promptRegistry.template?.prompt_name ?? requestInfo?.prompt_name;

  const analyzeSimilarLogsHref = useMemo(() => {
    if (workspaceId === null || workspaceId === undefined || !requestInfo) {
      return null;
    }
    const filters = buildFiltersFromRequestContext({
      prompt_id: requestInfo.prompt_id,
      prompt_name: promptName,
      prompt_version_number: requestInfo.prompt_version_number,
      engine: requestInfo.engine,
      tags_array: requestInfo.tags_array,
      tags: requestInfo.tags,
    });
    return buildWorkspaceAnalyticsPath({
      workspaceId: String(workspaceId),
      baseSearch: location.search,
      filters,
      dateRange: buildSimilarLogsDateRange(requestInfo.request_start_time),
      openAnalyticsTab: true,
    });
  }, [workspaceId, requestInfo, promptName, location.search]);

  useEffect(() => {
    if (!analyzeSimilarLogsHref) {
      return;
    }
    preloadAnalyticsPage();
  }, [analyzeSimilarLogsHref]);

  if (!requestInfo) {
    return <HistoryPageNavbarLoadingShimmer compactTitle={compactTitle} />;
  }

  const traceOrRequestLogLabel = isInTrace ? "View Request Log" : "View Trace";

  let traceOrRequestLogLink: string | null = null;

  if (workspaceId !== null && workspaceId !== undefined) {
    if (isInTrace) {
      traceOrRequestLogLink = `/workspace/${workspaceId}/request/${requestId}${preservedQuerySuffix}`;
    } else if (requestInfo.trace_id) {
      traceOrRequestLogLink = `/workspace/${workspaceId}/traces/${requestInfo.trace_id}${preservedQuerySuffix}`;
    }
  }

  const storeRequestsAnalyticsReturnUrl = () => {
    sessionStorage.setItem(
      SESSION_STORAGE.REQUESTS_ANALYTICS_RETURN_URL,
      `${location.pathname}${location.search}${location.hash}`,
    );
  };

  return (
    <div className="flex flex-1 flex-col gap-4 px-4 pb-4 md:px-0">
      <div className={getHistoryPageNavbarGridClassName(compactTitle)}>
        <div className="flex min-w-0 items-center gap-2 overflow-hidden">
          <h1 className={getHistoryPageNavbarTitleClassName(compactTitle)}>Request Log</h1>
          {isInTrace && traceOrRequestLogLink ? (
            <BasicTooltip content={traceOrRequestLogLabel} side="bottom">
              <Link
                to={traceOrRequestLogLink}
                aria-label={traceOrRequestLogLabel}
                className="shrink-0 rounded p-1 text-gray-500 transition-colors hover:bg-gray-100 hover:text-gray-700"
              >
                <ExternalLinkIcon className="h-4 w-4" />
              </Link>
            </BasicTooltip>
          ) : null}
        </div>
        <div className={getHistoryPageNavbarActionsClassName(compactTitle)}>
          {!isInTrace && traceOrRequestLogLink ? (
            <Link
              to={traceOrRequestLogLink}
              aria-label={traceOrRequestLogLabel}
              className="inline-flex min-w-[45px] shrink items-center gap-1 overflow-hidden whitespace-nowrap text-sm hover:text-gray-500"
            >
              <ExternalLinkIcon className="h-4 w-4 shrink-0" />
              <OverflowDisplayTooltip
                content={traceOrRequestLogLabel}
                side="bottom"
                className="min-w-0 truncate"
              >
                <span>View Trace</span>
              </OverflowDisplayTooltip>
            </Link>
          ) : null}
          {analyzeSimilarLogsHref ? (
            <LinkWithSearch
              path={analyzeSimilarLogsHref}
              onClick={storeRequestsAnalyticsReturnUrl}
              onPointerEnter={preloadAnalyticsPage}
              onFocus={preloadAnalyticsPage}
              className="inline-flex min-w-[45px] shrink items-center gap-1 overflow-hidden whitespace-nowrap text-sm hover:text-gray-500"
            >
              <ChartBarIcon className="h-4 w-4 shrink-0" />
              <OverflowDisplayTooltip
                content="Analyze Similar Logs"
                side="bottom"
                className="min-w-0 truncate"
              >
                <span>Analyze Similar Logs</span>
              </OverflowDisplayTooltip>
            </LinkWithSearch>
          ) : null}
          <ScoreDropdown
            requestId={requestId}
            requestScore={navbarState.requestScore}
            resetScore={navbarState.resetScore}
            score={navbarState.score}
            scores={navbarState.otherScoresList}
            setResetScore={navbarState.setResetScore}
            setScore={navbarState.setScore}
          />
          <HistoryPageNavbarMenu
            isLoggedIn={navbarState.isLoggedIn}
            isLoadingToggleStarred={navbarState.isLoadingToggleStarred}
            isStarred={Boolean(requestInfo.is_starred)}
            onOpenShareDialog={navbarState.onOpenShareDialog}
            onToggleStar={navbarState.onToggleStar}
          />
        </div>
        <div className={getHistoryPageNavbarMetadataClassName()}>
          <h4 className="shrink-0 whitespace-nowrap">Request ID: {requestInfo.id}</h4>
          <div className="hidden h-1 w-1 shrink-0 rounded-full bg-gray-300 sm:block" />
          <BasicTooltip
            content={navbarState.loggedTimeTitle}
            shouldShow={Boolean(navbarState.loggedTimeTitle)}
          >
            <h4 className="shrink-0 whitespace-nowrap">Logged: {navbarState.loggedTimeElement}</h4>
          </BasicTooltip>
          {requestInfo.logged_before_retention_policy ? (
            <>
              <div className="hidden h-1 w-1 shrink-0 rounded-full bg-gray-300 sm:block" />
              <RetentionPolicyBadge
                loggedBeforeRetentionPolicy={requestInfo.logged_before_retention_policy}
              />
            </>
          ) : null}
        </div>
        <div className={getHistoryPageNavbarResourceLinksClassName()}>
          <HistoryPageNavbarResourceLinks
            requestInfo={requestInfo}
            activeWorkspaceId={navbarState.activeWorkspaceId}
            promptRegistry={navbarState.promptRegistry}
            requestGroups={navbarState.requestGroups}
          />
        </div>
      </div>
      <HistoryPageNavbarShareDialog
        isLoading={navbarState.loading}
        isOpen={navbarState.isShareDialogOpen}
        onOpenChange={navbarState.setIsShareDialogOpen}
        onSharingEnabledChange={navbarState.onSetSharingEnabled}
        sharingEnabled={navbarState.sharingEnabled}
        sharingUrl={navbarState.sharingUrl}
        sharingUrlDisplay={navbarState.sharingUrlDisplay}
      />
    </div>
  );
};

export default HistoryPageNavbar;
